import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react"

import moment from "moment"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import { Button, Divider, Icon, Label, Message, Modal, Popup, Table } from "semantic-ui-react"
import SearchParams from "simplydo/core/searchParams"
import type { OpenAPI } from "simplydo/interfaces"

import api from "api"
import IdeaPdfExport from "components/lib/IdeaPdfExport"
import { HandledIdeaPreview as IdeaPreview } from "components/lib/Ideas"
import { useTrayStatus } from "components/lib/Notifications"
import ProductTour from "components/lib/ProductTour"
import { FakeLink, TooltipButton } from "components/lib/UI"
import { useAppSelector } from "store"
import useTheme from "theme/useTheme"
import util from "utils/utils"

export const AssessmentClosureInfo = ({ ideaTemplate }: { ideaTemplate?: OpenAPI.Schemas["IdeaTemplate"] }) => {
  const { t } = useTranslation()

  if (!ideaTemplate) return null
  const { assessmentClosed = false, assessmentDeadline } = ideaTemplate
  if (assessmentClosed) {
    return (
      <Message warning size="small" style={{ marginBottom: 25 }}>
        <p>
          <b>Assessment closed:</b> Assessment is now closed for this {t("generic.challenge")}. You can continue to view
          your assigned ideas and previous assessments. If you think this is in error, please contact an administrator.
        </p>
      </Message>
    )
  }
  if (assessmentDeadline) {
    return (
      <Message info size="small" style={{ marginBottom: 25 }}>
        <p>
          <b>Assessment deadline:</b> Assessment for this {t("generic.challenge")} has a deadline of{" "}
          <b>{moment(assessmentDeadline).format("HH:mm on DD/MM/YY")}</b>. You cannot edit or submit assessments after
          this date.
        </p>
      </Message>
    )
  }
  return null
}

type AssessmentIdeas = OpenAPI.GET<"/challenges/{id}/assessments">["response"]["ideas"]

type IChallengeYourAssessments = {
  challenge: OpenAPI.Schemas["Challenge"] & OpenAPI.Schemas["AssignChallengeAssessmentDetail"]
  getAssignedAssessments: () => void
  myAssignedAssessments: AssessmentIdeas
  setMyAssignedAssessments: Dispatch<SetStateAction<AssessmentIdeas>>
  demoingTour?: string
}

function ChallengeYourAssessments({
  getAssignedAssessments,
  myAssignedAssessments,
  setMyAssignedAssessments,
  challenge,
  demoingTour,
}: IChallengeYourAssessments) {
  const searchParams = new SearchParams(window.location.search)
  const user = useAppSelector((state) => state.user)
  const urlOrganisation = useSelector(
    (state: { organisations: { urlOrganisation: OpenAPI.Schemas["Organisation"] | null } }) =>
      state?.organisations?.urlOrganisation,
  )
  const [previewId, setPreviewId] = useState(searchParams.has("idea") ? searchParams.get("idea") : "")
  const [assessmentEdited, setAssessmentEdited] = useState(false)
  const [tourCheckLoaded, setTourCheckLoaded] = useState(false)
  const [assessmentSubmissionModalOpen, setAssessmentSubmissionModalOpen] = useState(false)

  const [_, setTrayStatus] = useTrayStatus()
  const shouldShowTour = useMemo(
    () => !!challenge?._id && !!myAssignedAssessments.length,
    [challenge, myAssignedAssessments],
  )
  const tourIdea = useMemo(
    () => (myAssignedAssessments.length ? myAssignedAssessments[0]._id : ""),
    [myAssignedAssessments],
  )
  const { ownerIdeaTemplate } = challenge
  // @ts-ignore
  const { assessmentClosed = false } = ownerIdeaTemplate
  const challengeId = challenge?._id
  const userId = user?._id
  const navigate = useNavigate()
  const theme = useTheme()

  const { t } = useTranslation()

  useEffect(() => {
    if (previewId) setAssessmentEdited(false)
  }, [previewId])

  // If we are about to run the assessment tour, we do not display the idea preview
  const getTour = useCallback(() => {
    if ((user?.ownerOrganisation?._id || urlOrganisation) && !theme.sizes.isMobile) {
      api.organisations.getProductTour(
        user?.ownerOrganisation?._id || urlOrganisation,
        "assessment",
        ({ success, userHasCompletedTour }) => {
          if (success && (!userHasCompletedTour || demoingTour === "assessment")) {
            navigate({ search: "" }, { replace: true })
            setPreviewId("")
          }
          setTourCheckLoaded(true)
        },
        () => setTourCheckLoaded(true),
      )
    } else {
      setTourCheckLoaded(true)
    }
  }, [user, demoingTour, urlOrganisation, navigate, theme.sizes.isMobile])

  useEffect(() => {
    getTour()
  }, [getTour])

  const onModalClose = useCallback(() => {
    navigate({ search: "" }, { replace: true })
    setPreviewId("")
    if (assessmentEdited) getAssignedAssessments()
  }, [assessmentEdited, getAssignedAssessments, navigate])

  const submitAssessment = (ideaId) => {
    util
      .confirm(
        "Ready to submit your assessment?",
        `Once submitted you will not be able to change your assessment of this ${t("generic.idea")}.`,
      )
      .then(() =>
        api.ideas.submitAssessment(
          ideaId,
          ({ idea: updatedIdea }) => {
            toast.success("Assessment submitted")
            setMyAssignedAssessments((prevState) =>
              prevState.map((ideaAssessment) => {
                if (ideaAssessment._id !== ideaId) return ideaAssessment
                return { ...ideaAssessment, ...updatedIdea }
              }),
            )
          },
          (err) => {
            toast.error(`Unable to submit your assessment: ${err.message}`)
          },
        ),
      )
      .catch(() => {})
  }

  const submitAllAssessments = useCallback(() => {
    util
      .confirm(
        "Ready to submit your assessments?",
        `Once submitted you will not be able to change any of your assessments in this ${t("generic.challenge")}. Only completed assessments will be submitted.`,
      )
      .then(() => {
        api.challenges.submitAllAssessments(
          challengeId,
          ({ submittedAssessmentIdeaIds }) => {
            toast.success("Assessments submitted")
            setMyAssignedAssessments((prevState) =>
              prevState.map((ideaAssessment) => {
                if (submittedAssessmentIdeaIds.indexOf(ideaAssessment._id) > -1) {
                  return {
                    ...ideaAssessment,
                    assessments: ideaAssessment.assessments.map((prev) => ({ ...prev, isSubmitted: true })),
                  }
                }
                return ideaAssessment
              }),
            )
          },
          (err) => toast.error(`Unable to submit your assessment: ${err.message}`),
        )
      })
      .catch(() => {})
  }, [challengeId, setMyAssignedAssessments, t])

  const downloadReport = () => {
    const challengeName = challenge?.name || "Challenge"
    api.users.createDownload(
      user._id,
      {
        downloadType: "userAssessmentXlsxExport",
        displayName: `${challengeName} assessment report.xlsx`,
        challenge: challenge._id,
      },
      (download) => {
        api.queryClient.setQueryData(["get", "users", "downloads"], (prev: any) => ({
          ...prev,
          pendingDownloads: (prev?.pendingDownloads || []).concat([download]),
        }))
        setTrayStatus({ open: true, activeIndex: 1 })
      },
      () => {},
    )
  }

  /* Note that myAssignedAssessments is a list of ideas, each with an assessment object, which might contain an assessments array. Each entry in the assessments array is an assessment made by an individual assessor, which might contain a (deeper) assessment object, which references individual idea sections.
   * Example myAssignedAssessments: [
   * {
   *    name: 'Idea name',
   *    _id: 'IdeaID',
   *    assessmentScore: 75,
   *    assessmentSubmittedScore: 75,
   *    assessments: [ // array of assessor's assessments
   *      {
   *        score: 75, // total calculated score across this assessor based on total available
   *        assessorUser: userObject,
   *        assessment: {
   *          'section1ID': {
   *            score: 15, // assessor's score for this section,
   *            notes: 'Notes for this section'
   *          }, ...
   *        },
   *      }, ...
   *    ]
   *  }, ...
   * ]
   *
   * Note that whilst other assessor's assessments may be present in the DB and their names included in the array, if scoped to a single assessor then the total calcualted score will only take into account the scoped assessment.
   */
  const unsubmittedAssessments = useMemo(
    () => myAssignedAssessments.filter((assessment) => assessment.assessments.find((x) => x.isSubmitted !== true)),
    [myAssignedAssessments],
  )

  const submissableAssessments = useMemo(
    () =>
      unsubmittedAssessments.filter((assessment) => {
        const myAssessment = assessment.assessments?.find((ideaAssessment) => ideaAssessment.user === userId)
        const assessmentIsSubmissable = util.assessmentIsSubmissable(myAssessment, ownerIdeaTemplate, assessmentClosed)
        return assessmentIsSubmissable
      }),
    [unsubmittedAssessments, userId, ownerIdeaTemplate, assessmentClosed],
  )

  const unsubmittableAssessments = useMemo(
    () =>
      unsubmittedAssessments.filter((assessment) => {
        const myAssessment = assessment.assessments?.find((ideaAssessment) => ideaAssessment.user === userId)
        const assessmentIsSubmissable = util.assessmentIsSubmissable(myAssessment, ownerIdeaTemplate, assessmentClosed)
        return !assessmentIsSubmissable
      }),
    [unsubmittedAssessments, userId, ownerIdeaTemplate, assessmentClosed],
  )

  const checkAllSubmissibiliy = useCallback(() => {
    if (submissableAssessments.length === unsubmittedAssessments.length) {
      submitAllAssessments()
    } else {
      setAssessmentSubmissionModalOpen(true)
    }
  }, [submitAllAssessments, unsubmittedAssessments, submissableAssessments])

  const assessmentIds = useMemo(() => myAssignedAssessments.map((a) => a._id), [myAssignedAssessments])
  const currentPreviewIndex = useMemo(() => assessmentIds.indexOf(previewId), [assessmentIds, previewId])

  const moveIdea = useCallback(
    (direction) => {
      if (direction === 1) {
        setPreviewId(assessmentIds[currentPreviewIndex + 1])
      } else {
        setPreviewId(assessmentIds[currentPreviewIndex - 1])
      }
    },
    [currentPreviewIndex, assessmentIds],
  )
  return (
    <>
      {/* @ts-ignore */}
      <IdeaPreview
        ideaId={tourCheckLoaded && previewId}
        onAssessmentEdited={() => setAssessmentEdited(true)}
        closeModal={() => onModalClose()}
        canGoNextIdea={currentPreviewIndex < myAssignedAssessments.length - 1}
        canGoPrevIdea={currentPreviewIndex > 0}
        moveIdea={moveIdea}
        totalIdeas={myAssignedAssessments.length}
        currentIdeaPosition={currentPreviewIndex + 1}
        page={1}
        limit={myAssignedAssessments.length}
      />
      <ProductTour
        tour="assessment"
        skipStep={!ownerIdeaTemplate?.assessmentDeadline && ".assessment-tour-step-5"}
        isReady={shouldShowTour}
        customTourFunctions={{
          openIdea: () => setPreviewId(tourIdea),
          closeIdea: () => {
            onModalClose()
          },
        }}
        onComplete={() => setPreviewId("")}
      />
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={assessmentSubmissionModalOpen}
        onClose={() => setAssessmentSubmissionModalOpen(false)}
      >
        <Modal.Header>Assessment submission</Modal.Header>
        <Modal.Content>
          <h4>
            {" "}
            You have{" "}
            {util.pluralise(submissableAssessments?.length || 0, "completed assessment", "completed assessments")} and{" "}
            {util.pluralise(unsubmittableAssessments?.length || 0, "oustanding assessment", "oustanding assessments")}.
            Only completed assessments will be submitted.
          </h4>
          <p>
            Below are the assessments that will <b>not</b> be included in your submission:
          </p>
          <ul>
            {unsubmittableAssessments?.map((a) => {
              return <li key={a._id}>{a.name}</li>
            })}
          </ul>
        </Modal.Content>
        <Modal.Actions>
          <Button content="Cancel" onClick={() => setAssessmentSubmissionModalOpen(false)} />
          <Button
            primary
            icon="check"
            content="Submit only the completed assessments"
            disabled={submissableAssessments?.length === 0}
            onClick={() => {
              setAssessmentSubmissionModalOpen(false)
              submitAllAssessments()
            }}
          />
        </Modal.Actions>
      </Modal>
      {!user ? (
        <Message>
          <h3>Please login to view this page</h3>
          <p>Register or login to your SimplyDo account to view your assessments for this {t("generic.challenge")}.</p>
          <Divider section />
          <Button as={Link} to={`/login?then=/challenges/${challenge?._id}/assessments`} primary>
            Login or register
          </Button>
        </Message>
      ) : (
        <>
          <div className="assessment-tour-step-1">
            <div className="assessment-tour-step-5" style={{ margin: 0 }}>
              <AssessmentClosureInfo ideaTemplate={ownerIdeaTemplate} />
            </div>
            <Table unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("common:capitalise", { key: "generic.idea" })}</Table.HeaderCell>
                  <Table.HeaderCell>
                    Total score
                    <Popup
                      position="top right"
                      trigger={<Icon name="question circle" style={{ marginLeft: 5 }} />}
                      content="Automatically calculated based on your assessment & predefined section weightings"
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell>Conflict</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  {!theme.sizes.isMobile && <Table.HeaderCell />}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {myAssignedAssessments?.map((a, index) => {
                  const myAssessment: AssessmentIdeas[number]["assessments"][number] = a.assessments?.find(
                    (ideaAssessment) => ideaAssessment.user === userId,
                  )
                  const assessmentIsSubmissable = util.assessmentIsSubmissable(
                    myAssessment,
                    ownerIdeaTemplate,
                    assessmentClosed,
                  )
                  return (
                    <Table.Row key={a._id} className={"assessment-tour-step-2"}>
                      <Table.Cell>
                        {/* @ts-ignore */}
                        <FakeLink onClick={() => setPreviewId(a._id)}>{a.name}</FakeLink>
                      </Table.Cell>
                      <Table.Cell>
                        {myAssessment?.conflictOfInterest?.hasConflictOfInterest && myAssessment?.isSubmitted ? (
                          "N/A"
                        ) : (
                          <>{Math.round(myAssessment?.score || 0)} %</>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {myAssessment?.conflictOfInterest?.hasConflictOfInterest ? (
                          <Label size="small" color="red" basic>
                            Conflict of interest
                          </Label>
                        ) : (
                          "-"
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {myAssessment?.isSubmitted ? (
                          <Label size="small" color="green">
                            Submitted
                          </Label>
                        ) : (
                          <Label size="small" color="orange">
                            Not submitted
                          </Label>
                        )}
                      </Table.Cell>
                      {!theme.sizes.isMobile && (
                        <Table.Cell>
                          <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <IdeaPdfExport
                              ideas={[a as unknown as OpenAPI.Schemas["Idea"]]}
                              challenge={challenge as unknown as OpenAPI.Schemas["Challenge"]}
                              trigger={<Button size="tiny" basic icon="file pdf" />}
                            />
                            {!myAssessment?.isSubmitted && !assessmentClosed ? (
                              <TooltipButton
                                tooltip={
                                  assessmentIsSubmissable
                                    ? undefined
                                    : "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest"
                                }
                                disabled={!assessmentIsSubmissable}
                                size="tiny"
                                inverted
                                primary
                                basic={!assessmentIsSubmissable}
                                content="Submit assessment"
                                onClick={() => submitAssessment(a._id)}
                              />
                            ) : null}
                            <Button
                              primary={!myAssessment?.isSubmitted}
                              size="tiny"
                              content={
                                myAssessment?.isSubmitted ? `View ${t("generic.idea")}` : `Assess ${t("generic.idea")}`
                              }
                              className={index === 0 ? "assessment-tour-step-3" : null}
                              icon="eye"
                              onClick={() => setPreviewId(a._id)}
                            />
                          </div>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
              marginTop: 20,
            }}
          >
            <Popup
              trigger={
                <Button
                  size="tiny"
                  secondary
                  basic
                  content="Download your assessment summary"
                  icon="file excel"
                  onClick={() => downloadReport()}
                />
              }
              content="Generate an excel report containing a summary of your assessments"
            />
            <IdeaPdfExport
              ideas={myAssignedAssessments as unknown as OpenAPI.Schemas["Idea"][]}
              challenge={challenge as unknown as OpenAPI.Schemas["Challenge"]}
              trigger={
                <Button
                  size="tiny"
                  secondary
                  basic
                  content={`Download all ${t("generic.ideas")} as PDF`}
                  icon="file pdf"
                />
              }
            />
            {unsubmittedAssessments?.length > 0 && (
              <TooltipButton
                icon="check"
                primary
                size="small"
                content="Submit all assessments"
                className="assessment-tour-step-6"
                onClick={() => checkAllSubmissibiliy()}
              />
            )}
          </div>
        </>
      )}
    </>
  )
}

export default ChallengeYourAssessments
